import { z } from "zod";
import { TASK_STATUSES, type TaskStatus } from "./enums.js";
import type { TaskMaterialDto } from "./materials.js";

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = { open: "Open", bezig: "Bezig", klaar: "Klaar" };

const dueDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum als JJJJ-MM-DD")
  .nullish()
  .transform((v) => v ?? null);

/**
 * Uitvoerder: gebruiker uit de organisatie, bedrijf (en contactpersoon) uit het adresboek, óf vrije tekst.
 * Met een bedrijf of persoon zet de server de weergavenaam in `assigneeText`.
 */
export const taskCreateSchema = z.object({
  id: z.uuid(),
  title: z.string().trim().min(1, "Vul een titel in").max(120),
  assigneeUserId: z.uuid().nullish(),
  assigneeCompanyId: z.uuid().nullish(),
  assigneePersonId: z.uuid().nullish(),
  assigneeText: z
    .string()
    .trim()
    .max(120)
    .nullish()
    .transform((v) => (v ? v : null)),
  dueDate,
  issueIds: z.array(z.uuid()).max(500).default([]),
  /** Storingspost van een contract (afkoop storingen, docs/CONTRACTEN.md). */
  contractLineId: z.uuid().nullish(),
  /** Werkelijke kosten excl. btw in centen (Inzichten per leverancier). */
  costCents: z.number().int().min(0).max(100_000_000).nullish(),
});

export const taskUpdateSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    assigneeUserId: z.uuid().nullable(),
    assigneeCompanyId: z.uuid().nullable(),
    assigneePersonId: z.uuid().nullable(),
    assigneeText: z
      .string()
      .trim()
      .max(120)
      .nullable()
      .transform((v) => (v ? v : null)),
    dueDate,
    status: z.enum(TASK_STATUSES),
    issueIds: z.array(z.uuid()).max(500),
    contractLineId: z.uuid().nullable(),
    costCents: z.number().int().min(0).max(100_000_000).nullable(),
  })
  .partial();

export interface TaskDto {
  id: string;
  buildingId: string;
  code: string;
  title: string;
  assigneeUserId: string | null;
  /** Naam uit het adresboek, van de gebruiker, of de vrije tekst. */
  assigneeName: string | null;
  assigneeText: string | null;
  /** Vaste uitvoerder uit het adresboek. */
  assigneeCompanyId: string | null;
  assigneePersonId: string | null;
  dueDate: string | null;
  status: TaskStatus;
  issueIds: string[];
  /** Taak *Keuring plannen* bij een verplichting van een asset. */
  assetObligationId: string | null;
  /** Materialen (module Materialen); leeg zonder module. */
  materials: TaskMaterialDto[];
  /** Storingspost van een contract (alleen met de module Contracten). */
  contractLine: { contractId: string; contractName: string; lineId: string; lineDescription: string } | null;
  /** Werkelijke kosten excl. btw in centen. */
  costCents: number | null;
  createdAt: string;
}

/** "Mijn taken": met gebouw en de bevindingen erin. */
export interface MyTaskDto extends TaskDto {
  building: { id: string; code: string; name: string };
  issues: { id: string; code: string; title: string; status: string; priority: string }[];
}

export interface PersonDto {
  userId: string;
  name: string;
  role: string;
}
